/**
 * <countdown-collection> — sections/countdown-collection.liquid
 * Ticking countdown that hides itself once it runs out, plus reveal on scroll
 * and keyboard support for the mobile product slider.
 */
(function () {
  if (customElements.get("countdown-collection")) return;

  const MOBILE_QUERY = "(max-width: 989px)";
  const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

  function parseEndDate(value) {
    if (!value) return null;

    /* "YYYY-MM-DD HH:MM" is read in the visitor's own time zone */
    const parts = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2}))?/);
    if (parts) {
      return new Date(
        Number(parts[1]),
        Number(parts[2]) - 1,
        Number(parts[3]),
        Number(parts[4] || 0),
        Number(parts[5] || 0),
        0,
      );
    }

    const fallback = new Date(value);
    return Number.isNaN(fallback.getTime()) ? null : fallback;
  }

  class CountdownCollection extends HTMLElement {
    connectedCallback() {
      this.timer = this.querySelector("[data-cdc-timer]");
      this.status = this.querySelector("[data-cdc-status]");
      this.track = this.querySelector(".cdc_grid");
      this.panels = Array.from(this.querySelectorAll(".cdc_reveal"));
      this.items = Array.from(this.querySelectorAll(".cdc_item"));

      this.mobileQuery = window.matchMedia(MOBILE_QUERY);
      this.reducedMotionQuery = window.matchMedia(REDUCED_MOTION_QUERY);

      this.onKeydown = this.onKeydown.bind(this);
      this.syncTrackFocus = this.syncTrackFocus.bind(this);

      this.setupReveal();
      this.setupCountdown();
      this.setupSlider();
    }

    disconnectedCallback() {
      if (this.revealObserver) this.revealObserver.disconnect();
      if (this.interval) window.clearInterval(this.interval);
      if (this.track) {
        this.track.removeEventListener("keydown", this.onKeydown);
      }
      window.removeEventListener("resize", this.syncTrackFocus);
    }

    setupReveal() {
      if (!this.panels.length) return;

      if (this.reducedMotionQuery.matches || !("IntersectionObserver" in window)) {
        this.panels.forEach((panel) => panel.classList.add("is-in-view"));
        return;
      }

      this.revealObserver = new IntersectionObserver(
        (entries, observer) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add("is-in-view");
            observer.unobserve(entry.target);
          });
        },
        { rootMargin: "0px 0px -10% 0px", threshold: 0.1 },
      );

      this.panels.forEach((panel) => this.revealObserver.observe(panel));
    }

    setupCountdown() {
      if (!this.timer) return;

      this.endDate = parseEndDate(this.dataset.end);
      if (!this.endDate) return;

      this.values = {
        days: this.querySelector("[data-cdc-days]"),
        hours: this.querySelector("[data-cdc-hours]"),
        minutes: this.querySelector("[data-cdc-minutes]"),
        seconds: this.querySelector("[data-cdc-seconds]"),
      };

      this.lastAnnouncedMinute = null;

      if (!this.tick()) return;

      this.timer.hidden = false;
      this.interval = window.setInterval(() => this.tick(), 1000);
    }

    /* Returns false once the deadline has passed, which also hides the timer */
    tick() {
      const remaining = this.endDate.getTime() - Date.now();

      if (remaining <= 0) {
        if (this.interval) window.clearInterval(this.interval);
        this.timer.hidden = true;
        if (this.status) this.status.textContent = "";
        return false;
      }

      const totalSeconds = Math.floor(remaining / 1000);
      const days = Math.floor(totalSeconds / 86400);
      const hours = Math.floor((totalSeconds % 86400) / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;

      this.setValue(this.values.days, days);
      this.setValue(this.values.hours, hours);
      this.setValue(this.values.minutes, minutes);
      this.setValue(this.values.seconds, seconds);

      /* The ticking digits are hidden from screen readers, so announce whole minutes only */
      if (this.status && this.lastAnnouncedMinute !== minutes) {
        this.lastAnnouncedMinute = minutes;
        this.status.textContent = days + " days " + hours + " hours " + minutes + " minutes remaining";
      }

      return true;
    }

    setValue(element, value) {
      if (!element) return;

      const next = String(value).padStart(2, "0");
      if (element.textContent === next) return;

      element.textContent = next;

      if (this.reducedMotionQuery.matches) return;

      element.classList.remove("is-ticking");
      /* Reading offsetWidth restarts the animation on the next value */
      void element.offsetWidth;
      element.classList.add("is-ticking");
    }

    setupSlider() {
      if (!this.track) return;

      this.track.addEventListener("keydown", this.onKeydown);
      window.addEventListener("resize", this.syncTrackFocus, { passive: true });
      this.syncTrackFocus();
    }

    get isScrollable() {
      return this.track ? this.track.scrollWidth - this.track.clientWidth > 2 : false;
    }

    syncTrackFocus() {
      if (!this.track) return;

      if (this.mobileQuery.matches && this.isScrollable) {
        this.track.setAttribute("tabindex", "0");
        this.track.setAttribute("role", "region");
        this.track.setAttribute("aria-label", this.trackLabel);
      } else {
        this.track.removeAttribute("tabindex");
        this.track.removeAttribute("role");
        this.track.removeAttribute("aria-label");
      }
    }

    get trackLabel() {
      const heading = this.querySelector(".cdc_heading");
      return heading ? heading.textContent.trim() : "Products";
    }

    onKeydown(event) {
      if (!this.isScrollable) return;
      if (event.target !== this.track) return;
      if (event.key !== "ArrowRight" && event.key !== "ArrowLeft") return;

      event.preventDefault();

      const step = this.items[0] ? this.items[0].getBoundingClientRect().width + 12 : 200;
      const direction = event.key === "ArrowRight" ? 1 : -1;
      const behavior = this.reducedMotionQuery.matches ? "auto" : "smooth";

      this.track.scrollBy({ left: step * direction, behavior });
    }
  }

  customElements.define("countdown-collection", CountdownCollection);
})();
